/**
 * Quotes y Orders de un contacto en un store (para los side panels del head del chat).
 * Reusa el main API (`/quotes`, `/orders`) con filtros de nestjs-paginate y el access_token.
 */
import { axiosInstance } from "@/utilities";
import { API_BASE_URL, API_URL_USER_ADDRESSES_V1 } from "@/services/XCONS_ENV";

export interface QuoteBrief {
  id: number;
  name: string;
  state?: string;
  storeCode?: string;
  sellerCode?: string;
  updatedAt?: string;
  total: number;
  /**
   * Lo necesario para armar el link público del presupuesto (N5), el mismo que comparte la ficha:
   * `storeLink + /marketplace/quote/view/uid/ + shareHash`. Sin estos dos no se puede compartir
   * desde el panel y habría que ir a buscar el presupuesto a su ficha.
   */
  shareHash?: string;
  storeLink?: string;
  /** Nombre del store para el mensaje de compartir (P3): el code no se le muestra al cliente. */
  storeName?: string;
}

export interface OrderShipmentBrief {
  deliveryMethod: string;
  status: string;
}

export interface OrderBrief {
  orderId: number;
  incrementId: string;
  name?: string;
  status?: string;
  storeCode?: string;
  sellerCode?: string;
  createdAt?: string;
  updatedAt?: string;
  grandTotal: number;
  invoiceStatus?: string;
  shipments: OrderShipmentBrief[];
}

/**
 * Estado de quote → label + color HEX (para badge outlined, Bug 31/32 #109).
 * Estados válidos del diseño: Customer / Bought On / Bought Off / Following.
 * `bought_assisted` se mapea a "Bought On" (no existe "Bought (assisted)" en el Figma).
 */
export function quoteStatusTag(state?: string): { label: string; color: string; bg: string; borderColor: string } | null {
  switch (state) {
    case "quoting":
    case "store":
    case "open_store":
      return { label: "Store", color: "#13c2c2", bg: "#e6fffb", borderColor: "#87e8de" };
    case "customer":
    case "customer_store":
      return { label: "Cliente", color: "#722ed1", bg: "#f9f0ff", borderColor: "#d3adf7" };
    case "follow":
      return { label: "Seguimiento", color: "#fa8c16", bg: "#fff7e6", borderColor: "#ffd591" };
    case "buy_on":
      return { label: "Comprado On", color: "#52c41a", bg: "#f6ffed", borderColor: "#b7eb8f" };
    case "bought_assisted":
      return { label: "Asistido", color: "#468c69", bg: "#e9f4ef", borderColor: "#abdbc1" };
    case "buy_off":
      return { label: "Comprado Off", color: "#15744b", bg: "#e9f4ef", borderColor: "#468c69" };
    case "lost":
      return { label: "Perdido", color: "#f5222d", bg: "#fff1f0", borderColor: "#ffa39e" };
    case "archived":
      return { label: "Archivado", color: "rgba(0,0,0,0.88)", bg: "#fff", borderColor: "#d9d9d9" };
    default:
      return state ? { label: state, color: "#8c8c8c", bg: "#f5f5f5", borderColor: "#d9d9d9" } : null;
  }
}

/** Estado de order → label en español + color de dot. */
export function orderStatusDot(status?: string): { label: string; color: string } {
  const s = (status ?? "").toLowerCase();
  if (s.includes("complete") || s.includes("closed")) return { label: "Completado", color: "#52c41a" };
  if (s.includes("cancel")) return { label: "Cancelado", color: "#ff4d4f" };
  return { label: "Abierto", color: "#faad14" };
}

/** snake_case/kebab del backend → sentence case (Bug 35): "pending_payment" → "Pending payment". */
export function formatStatusLabel(s?: string): string {
  if (!s) return "—";
  const clean = s.replace(/[_-]+/g, " ").trim().toLowerCase();
  return clean ? clean.charAt(0).toUpperCase() + clean.slice(1) : "—";
}

const ES_STATUS: Record<string, string> = {
  "Approved": "Aprobado",
  "Invoiced": "Facturado",
  "Pending payment": "Pago pendiente",
  "Payment review": "Revisión de pago",
  "Review": "Revisión de pago",
  "Payment verified": "Pago verificado",
  "Verified": "Pago verificado",
  "Pending": "Pendiente",
  "Pending delivery": "Pendiente de entrega",
  "Preparing order": "Preparando pedido",
  "In transit": "En tránsito",
  "Ready to ship": "Listo para enviar",
  "Delivered": "Entregado",
  "Cancel request": "Solicitud de cancelación",
  "Pending cancel": "Pendiente de cancelación",
  "Cancelled": "Cancelada",
  "Canceled": "Cancelada",
  "Complete": "Completado",
  "Processing": "En proceso",
  "Closed": "Cerrado",
};

/** snake_case del backend → label en español. Fallback a sentence case. */
export function translateStatusLabel(s?: string): string {
  const label = formatStatusLabel(s);
  return ES_STATUS[label] ?? label;
}

const PAYMENT_STATUS_ES: Record<string, string> = {
  "Pending": "Pago pendiente",
  "Review": "Revisión de pago",
  "Verified": "Pago verificado",
  "Invoiced": "Facturado - Aprobado",
};

const SHIPMENT_STATUS_ES: Record<string, string> = {
  "Pending": "Pendiente de entrega",
  "Preparing": "Preparando pedido",
  "Ready to deliver": "Listo para enviar",
};

/** Igual que translateStatusLabel pero con semántica de pago (ej. "Pending" → "Pago pendiente"). */
function translatePaymentStatus(s?: string): string {
  const label = formatStatusLabel(s);
  return PAYMENT_STATUS_ES[label] ?? ES_STATUS[label] ?? label;
}

/** Igual que translateStatusLabel pero con semántica de envío (ej. "Pending" → "Pendiente de entrega"). */
function translateShipmentStatus(s?: string): string {
  const label = formatStatusLabel(s);
  return SHIPMENT_STATUS_ES[label] ?? ES_STATUS[label] ?? label;
}

/** deliveryMethod (texto libre) → tipo de entrega + si es retiro en tienda (Bug 36). */
export function deliveryKind(method?: string): { label: string; pickup: boolean } {
  const m = (method ?? "").toLowerCase();
  const pickup = /retiro|pick\s?up|dep[oó]sito|store/.test(m);
  return { label: pickup ? "Store Pick Up" : "Fleet", pickup };
}

/** Color HEX de un badge de estado (invoice/entrega) según su texto (Bug 31/35 + #113). */
export function statusColor(label?: string): string {
  const s = (label ?? "").toLowerCase();
  if (s.includes("approved") || s.includes("transit") || s.includes("ship")) return "#08979c";
  if (s.includes("deliver") || s.includes("complete")) return "#389e0d";
  if (s.includes("pending") || s.includes("cancel")) return "#d46b08";
  return "#8c8c8c";
}

/** Estilo filled/outlined de badge de pedido según estado (Figma Gestion>PEDIDOS 12705-130423). */
export function orderBadgeStyle(status?: string): { bg: string; color: string; borderColor: string } {
  const s = (status ?? "").toLowerCase();
  // Pago y facturación — orden específico antes del genérico
  if (s.includes("revisión de pago") || s.includes("payment review"))
    return { bg: "#08979c", color: "#fff", borderColor: "#00474f" };
  if (s.includes("pago verificado") || s.includes("payment verified"))
    return { bg: "#e6fffb", color: "#00474f", borderColor: "#08979c" };
  if (s.includes("pago pendiente") || s.includes("pending payment"))
    return { bg: "#00474f", color: "#fff", borderColor: "#00474f" };
  if (s.includes("facturado") || s.includes("invoiced"))
    return { bg: "#fff", color: "#00474f", borderColor: "#00474f" };
  // Cancelación — específico antes de genérico
  if (s.includes("solicitud de cancelación") || s.includes("cancel request"))
    return { bg: "#ff4d4f", color: "#fff", borderColor: "#d9363e" };
  if (s.includes("cancelado") || s.includes("cancelled") || s.includes("canceled"))
    return { bg: "#fff", color: "#d9363e", borderColor: "#d9363e" };
  // Entregas
  if (s.includes("pendiente de entrega") || s.includes("pending delivery"))
    return { bg: "#002c8c", color: "#fff", borderColor: "#002c8c" };
  if (s.includes("preparando pedido") || s.includes("preparing order"))
    return { bg: "#0958d9", color: "#fff", borderColor: "#002c8c" };
  if (s.includes("listo para enviar") || s.includes("ready to ship"))
    return { bg: "#91caff", color: "#002c8c", borderColor: "#002c8c" };
  if (s.includes("tránsito") || s.includes("transit"))
    return { bg: "#e6f4ff", color: "#002c8c", borderColor: "#002c8c" };
  if (s.includes("entregado") || s.includes("delivered"))
    return { bg: "#fff", color: "#002c8c", borderColor: "#002c8c" };
  return { bg: "#fff", color: "#8c8c8c", borderColor: "#d9d9d9" };
}

export const contextApi = {
  /**
   * Presupuestos del contacto en el store. Se filtra por **participación** (contactId) igual que
   * el módulo de Quotes ("Relación: <contacto>"), NO por quote.userId (ese es el dueño del quote).
   */
  quotesByContact: async (contactId: number, storeCode: string): Promise<QuoteBrief[]> => {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/quotes`, {
      params: {
        "filter.participations.contactId": `$eq:${contactId}`,
        "filter.store.code": `$in:${storeCode}`,
        limit: 50,
        sortBy: "updatedAt:DESC",
      },
    });
    const rows: any[] = data?.data ?? [];
    return rows.map((q) => {
      const sellers = q.quoteSellers ?? [];
      return {
        id: q.id,
        name: q.name ?? `#${q.id}`,
        state: sellers[0]?.state,
        shareHash: q.shareHash,
        storeLink: q.store?.link,
        storeName: q.store?.name,
        storeCode: q.store?.code ?? storeCode,
        sellerCode: sellers[0]?.sellerCode,
        updatedAt: q.updatedAt,
        total: sellers.reduce((acc: number, s: any) => acc + Number(s?.total ?? 0), 0),
      };
    });
  },

  /** Pedidos del contacto (customerId = entity_id) en el store, con facturación y entregas. */
  ordersByContact: async (customerId: number, storeCode: string): Promise<OrderBrief[]> => {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/orders`, {
      params: {
        "filter.customerId": `$eq:${customerId}`,
        "filter.storeCode": `$in:${storeCode}`,
        limit: 50,
        sortBy: "createdAt:DESC",
      },
    });
    const rows: any[] = data?.data ?? [];
    return rows.map((o) => {
      const payment = (o.payments ?? [])[0];
      const invoice = (payment?.invoices ?? [])[0];
      return {
        orderId: o.orderId,
        incrementId: o.incrementId,
        name: o.name ?? o.quoteName,
        status: o.status,
        storeCode: o.storeCode ?? o.store?.code ?? storeCode,
        sellerCode: o.sellerCode ?? o.seller?.code ?? o.seller?.name,
        createdAt: o.createdAt,
        updatedAt: o.updatedAt,
        grandTotal: Number(o.grandTotal ?? 0),
        invoiceStatus: invoice?.status
          ? `Facturado - ${translateStatusLabel(invoice.status)}`
          : payment?.status
            ? translatePaymentStatus(payment.status)
            : undefined,
        shipments: (o.shipments ?? []).map((s: any) => ({
          deliveryMethod: s.deliveryMethod ?? "Envío",
          status: translateShipmentStatus(s.status) ?? "—",
        })),
      } as OrderBrief;
    });
  },

  /**
   * Las direcciones de envío del cliente en el store (#519), para la variable «Dirección de entrega»
   * de una plantilla. La misma agenda que usa el presupuesto (`QuoteAddressBookPopup`).
   */
  deliveryAddresses: async (userId: number, storeCode: string): Promise<{ id: number; linea: string; nombre: string }[]> => {
    const { data } = await axiosInstance.get(API_URL_USER_ADDRESSES_V1, {
      params: { user_id: userId, type: "shipping", ...(storeCode.trim() ? { store: storeCode.trim() } : {}) },
    });
    const rows: any[] = Array.isArray(data) ? data : data?.data ?? [];
    // Un "-" es relleno de campos que no aplican, no un dato (ver `QuoteAddressBookPopup`).
    const campo = (v: unknown) => {
      const s = String(v ?? "").trim();
      return /^-+$/.test(s) ? "" : s;
    };
    return rows
      .map((a) => ({
        id: Number(a.id),
        linea: [[campo(a.street1), campo(a.street2)].filter(Boolean).join(" "), campo(a.cityName), campo(a.provinceName), campo(a.postcode)]
          .filter(Boolean)
          .join(", "),
        nombre: [campo(a.firstname), campo(a.lastname)].filter(Boolean).join(" "),
      }))
      .filter((a) => a.linea);
  },

  /** Cantidad de presupuestos del contacto en el store desde `since`. Usa meta.totalItems. */
  quotesCountSince: async (contactId: number, storeCode: string, since: Date): Promise<number> => {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/quotes`, {
      params: {
        "filter.participations.contactId": `$eq:${contactId}`,
        "filter.store.code": `$in:${storeCode}`,
        "filter.updatedAt": `$gte:${since.toISOString()}`,
        limit: 1,
        page: 1,
      },
    });
    return data?.meta?.totalItems ?? 0;
  },

  /** Cantidad de pedidos del contacto en el store desde `since`. Usa meta.totalItems. */
  ordersCountSince: async (customerId: number, storeCode: string, since: Date): Promise<number> => {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/orders`, {
      params: {
        "filter.customerId": `$eq:${customerId}`,
        "filter.storeCode": `$in:${storeCode}`,
        "filter.createdAt": `$gte:${since.toISOString()}`,
        limit: 1,
        page: 1,
      },
    });
    return data?.meta?.totalItems ?? 0;
  },
};
